import { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { useAuth } from "@/hooks/use-auth";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
} from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  ChevronDown,
  LayoutDashboard,
  Moon,
  Sun,
  LogOut,
  Menu,
  Settings,
  User,
} from "lucide-react";
import { NotificationBell } from "@/components/notification/NotificationBell";
import { MessagingBell } from "@/components/messaging/MessagingBell";
import { useTheme } from "@/stores/theme-store";
import { useTeamStore } from "@/stores/team-store";
import { toast } from "sonner";
import { TeamSelector } from "@/components/team/TeamSelector";
import { SendFeedbackTrigger } from "@/components/feedback/SendFeedbackTrigger";
import { FeatureFlagsMenu } from "@/components/FeatureFlagsMenu";
import { NavSearch } from "@/components/search/NavSearch";
import { cn } from "@/lib/utils";
import {
  toDashboard,
  toHome,
  toInbox,
  toLogin,
  toPublicProfile,
  toSettings,
  toSignup,
  toTeamDashboard,
  toDiscover,
  toPeople,
  toProjects,
  toPortfolios,
  toFundingPools,
  isDiscoverPath,
  withDiscoverQuery,
} from "@/lib/urls";
import type { TeamSummaryDto } from "@/lib/tanstack-query/dtos/team.dto";
import { stringToGradient } from "@/lib/gradient";

const Navbar = () => {
  const { user, logout } = useAuth();
  const { currentTeam, setCurrentTeam } = useTeamStore();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await logout();
      toast.success("You have been logged out");
      navigate(toLogin());
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Failed to log out. Please try again.");
    } finally {
      setIsLoggingOut(false);
    }
  };

  // The per-type pages still exist; Discover is the main way in, so these
  // live under "Browse" instead of crowding the header.
  const browseItems = [
    { to: toPeople(), label: "People" },
    { to: toProjects(), label: "Projects" },
    { to: toPortfolios(), label: "Portfolios" },
    { to: toFundingPools(), label: "Funding Pools" },
  ];

  const currentPath = pathname.replace(/\/$/, "");
  // Discover has its own, larger search bar.
  const showNavSearch = currentPath !== toDiscover();

  const hasValidTeam = currentTeam && currentTeam.id !== "personal";
  const dashboardPath = hasValidTeam
    ? toTeamDashboard(currentTeam.id)
    : toDashboard();
  const onDashboard = currentPath.startsWith(dashboardPath);

  const getNavigationPath = (path: string) =>
    isDiscoverPath(path) ? withDiscoverQuery(path, searchParams) : path;

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const handleTeamSelect = (team: TeamSummaryDto | null) => {
    setCurrentTeam(team);
    navigate(team ? toTeamDashboard(team.id) : toDashboard());
  };

  const handleTeamCreate = (team: TeamSummaryDto) => {
    setCurrentTeam(team);
    navigate(toTeamDashboard(team.id));
  };

  return (
    <header className="relative z-50 w-full border-b bg-background">
      <div className="w-full px-3 sm:px-4 lg:px-8">
        <div className="flex h-14 sm:h-16 items-center">
          {/* Logo */}
          <div className="flex items-center shrink-0">
            <Link to={toHome()} className="flex items-center">
              <img
                src="/images/logo-mark.png"
                alt="Catalyze"
                className="h-8 w-auto sm:hidden"
              />
              <img
                src="/images/logo.png"
                alt="Catalyze"
                className="hidden sm:block max-h-8"
              />
            </Link>
          </div>

          {/* Primary actions: never collapse into the menu, only their
              labels drop on narrow screens. */}
          <div className="flex items-center gap-1 sm:gap-2 ml-2 sm:ml-4 lg:ml-6 shrink-0">
            <Button
              asChild
              variant="gradient"
              size="sm"
              className="rounded-full shadow-none hover:shadow-md hover:translate-y-0 min-h-touch px-3 sm:px-4"
            >
              <Link to={getNavigationPath(toDiscover())}>Discover</Link>
            </Button>
            {user && (
              <Button
                asChild
                variant="ghost"
                size="sm"
                className={cn(
                  "rounded-full min-h-touch px-3",
                  onDashboard && "bg-accent text-accent-foreground",
                )}
              >
                <Link
                  to={dashboardPath}
                  aria-label="Dashboard"
                  aria-current={onDashboard ? "page" : undefined}
                  className="flex items-center gap-1.5"
                >
                  <LayoutDashboard className="h-4 w-4 min-[390px]:hidden" />
                  <span className="hidden min-[390px]:inline">Dashboard</span>
                </Link>
              </Button>
            )}
          </div>

          {/* Secondary navigation */}
          <NavigationMenu className="hidden lg:flex ml-1 flex-none">
            <NavigationMenuList className="flex gap-1">
              <NavigationMenuItem>
                <DropdownMenu>
                  <DropdownMenuTrigger className="group flex items-center justify-center gap-1 py-2 px-3 text-sm font-medium text-muted-foreground transition-colors hover:text-primary data-[state=open]:text-primary whitespace-nowrap min-h-touch rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    Browse
                    <ChevronDown className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-44">
                    {browseItems.map((item) => (
                      <DropdownMenuItem key={item.to} asChild>
                        <Link
                          to={getNavigationPath(item.to)}
                          className="cursor-pointer min-h-touch flex items-center"
                        >
                          {item.label}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          {/* Site search: inline from sm up; its own row on phones */}
          {showNavSearch && (
            <NavSearch
              className={cn(
                "min-w-0 flex-1 max-w-md mx-2 sm:mx-3 lg:mx-4",
                user && "hidden md:block",
              )}
            />
          )}

          {/* Right side controls */}
          <div className="flex items-center gap-1 sm:gap-2 ml-auto shrink-0">
            {/* Team Switcher */}
            {user && (
              <div className="hidden lg:flex items-center gap-2">
                <TeamSelector
                  variant="desktop"
                  currentTeam={currentTeam}
                  onTeamSelect={handleTeamSelect}
                  onTeamCreate={handleTeamCreate}
                />
              </div>
            )}

            {/* Messages (in the menu on phones) */}
            {user && (
              <div className="hidden sm:flex">
                <MessagingBell />
              </div>
            )}

            {/* Notifications */}
            {user && <NotificationBell />}

            {/* Feedback */}
            {user && (
              <SendFeedbackTrigger
                variant="header-button"
                className="hidden lg:flex"
              />
            )}

            {/* User menu or auth buttons */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="h-8 w-8 sm:h-9 sm:w-9 p-0 shrink-0 min-w-touch min-h-touch"
                  >
                    <Avatar className="h-7 w-7 sm:h-8 sm:w-8">
                      {user.avatar && (
                        <AvatarImage
                          src={user.avatar}
                          alt={user.name}
                          className="object-cover"
                        />
                      )}
                      <AvatarFallback
                        className="text-white text-xs sm:text-sm"
                        style={{ background: stringToGradient(user.name) }}
                      >
                        {user.name?.charAt(0) ?? ""}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 sm:w-56">
                  <DropdownMenuLabel className="truncate">
                    {user.name ?? ""}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link
                      to={toPublicProfile(user.handle ?? user.id)}
                      className="min-h-touch flex items-center gap-2 cursor-pointer"
                    >
                      <User className="w-4 h-4" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      to={toSettings()}
                      className="min-h-touch flex items-center gap-2 cursor-pointer"
                    >
                      <Settings className="w-4 h-4" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      setTheme(theme === "dark" ? "light" : "dark");
                    }}
                    className="flex items-center gap-2 cursor-pointer min-h-touch"
                  >
                    <Sun className="h-4 w-4 sm:h-5 sm:w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                    <Moon className="absolute h-4 w-4 sm:h-5 sm:w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                    <span>Theme</span>
                  </DropdownMenuItem>
                  <FeatureFlagsMenu />

                  {user && (
                    <>
                      <DropdownMenuSeparator className="lg:hidden" />
                      <SendFeedbackTrigger
                        variant="menu-item"
                        className="lg:hidden"
                      />
                    </>
                  )}

                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                    className="flex items-center gap-2 cursor-pointer min-h-touch"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>{isLoggingOut ? "Logging out..." : "Log out"}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link to={toLogin()}>
                  <Button variant="outline" size="sm" className="min-h-touch">
                    Log In
                  </Button>
                </Link>
                <Link to={toSignup()}>
                  <Button size="sm" className="min-h-touch">
                    Sign Up
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu */}
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="lg:hidden shrink-0 min-w-touch min-h-touch p-2"
                >
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] sm:w-[350px] p-0">
                <SheetTitle className="hidden">Navigation</SheetTitle>
                <div className="flex flex-col h-full">
                  <div className="p-4 border-b">
                    <img
                      src="/images/logo.png"
                      alt="Catalyze"
                      className="h-6 w-auto"
                    />
                  </div>

                  {/* Team Switcher in Mobile */}
                  {user && (
                    <div className="p-4 border-b">
                      <TeamSelector
                        variant="mobile"
                        currentTeam={currentTeam}
                        onTeamSelect={handleTeamSelect}
                        onTeamCreate={handleTeamCreate}
                      />
                    </div>
                  )}

                  <nav className="flex flex-col flex-1 p-4">
                    <div className="space-y-1">
                      <Link
                        to={getNavigationPath(toDiscover())}
                        onClick={closeMobileMenu}
                        className="flex items-center py-3 px-4 text-base font-medium text-foreground hover:text-primary hover:bg-muted rounded-md transition-colors min-h-touch"
                      >
                        Discover
                      </Link>
                      {user && (
                        <Link
                          to={dashboardPath}
                          onClick={closeMobileMenu}
                          className="flex items-center py-3 px-4 text-base font-medium text-foreground hover:text-primary hover:bg-muted rounded-md transition-colors min-h-touch"
                        >
                          Dashboard
                        </Link>
                      )}
                      {user && (
                        <Link
                          to={toInbox()}
                          onClick={closeMobileMenu}
                          className="flex sm:hidden items-center py-3 px-4 text-base font-medium text-foreground hover:text-primary hover:bg-muted rounded-md transition-colors min-h-touch"
                        >
                          Messages
                        </Link>
                      )}
                    </div>

                    <p className="mt-4 mb-1 px-4 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Browse
                    </p>
                    <div className="space-y-1">
                      {browseItems.map((item) => (
                        <Link
                          key={item.to}
                          to={getNavigationPath(item.to)}
                          onClick={closeMobileMenu}
                          className="flex items-center py-2.5 px-4 text-base text-foreground hover:text-primary hover:bg-muted rounded-md transition-colors min-h-touch"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>

                    {!user && (
                      <div className="mt-6 pt-6 border-t space-y-3">
                        <Link
                          to={toLogin()}
                          onClick={closeMobileMenu}
                          className="block"
                        >
                          <Button
                            variant="outline"
                            className="w-full min-h-touch"
                          >
                            Log In
                          </Button>
                        </Link>
                        <Link
                          to={toSignup()}
                          onClick={closeMobileMenu}
                          className="block"
                        >
                          <Button className="w-full min-h-touch">
                            Sign Up
                          </Button>
                        </Link>
                      </div>
                    )}
                  </nav>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
        {/* Logged in, a phone's top row is full (Discover, Dashboard,
            notifications, avatar, menu), so search gets its own row. */}
        {showNavSearch && user && (
          <div className="pb-2.5 md:hidden">
            <NavSearch fullWidth />
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
